import React, { useState, useEffect, useMemo, useCallback } from 'react';

import { EditOutlined, SaveOutlined, UndoOutlined, LockOutlined, ExclamationCircleOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Card, Select, Button, Space, Divider, Typography, message, Modal, Alert, Tag, Popconfirm, Input, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';

import { filterGrantable } from '@utils/permissionSubset';
import { checkServiceResult } from '@utils/serviceResult';
import {
  ACTIONS,
  RESOURCES_BY_ROLE,
  RESOURCE_ACTIONS_BY_ROLE,
} from '../../../permissions_manifest.generated';

import PermissionMatrix from './PermissionMatrix';

const { Text } = Typography;
const { confirm } = Modal;

/**
 * 權限編輯組件
 * 用於編輯用戶或 Operator 的權限
 *
 * @param {Array} permissions - 當前用戶的權限列表
 * @param {Function} onSave - 保存權限的回調函數
 * @param {Object} templateService - injected service ({ fetchList, upsert, delete })
 * @param {String} role - 'admin' 或 'operator'
 * @param {Boolean} canEdit - 是否可以編輯（基於當前用戶的權限）
 * @param {Boolean} isEditingSelf - 是否在編輯自己
 */
const PermissionsEditor = ({
  permissions = [],
  onSave,
  templateService,
  role = 'admin',
  canEdit = false,
  isEditingSelf = false,
  grantableBy = null,
}) => {
  const { t } = useTranslation();

  // current = 已儲存的權限（server state）
  const [currentPermissions, setCurrentPermissions] = useState([]);
  // selectedPermissions = 預覽中的權限（working state）
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [saving, setSaving] = useState(false);

  // 組織 Permission Template 列表
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  // Save as Template 的 Modal 狀態
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [templateNameInput, setTemplateNameInput] = useState('');
  const [templateSaving, setTemplateSaving] = useState(false);

  // 套用 template 時被過濾掉的權限（因為超過 granter 可授權範圍）
  const [strippedFromTemplate, setStrippedFromTemplate] = useState([]);

  // 載入此 scope 的 Permission Template 列表
  const fetchTemplates = useCallback(async () => {
    try {
      const data = await templateService.fetchList();
      setTemplates(data.data || []);
    } catch (error) {
      console.error('Failed to load permission templates:', error);
    }
  }, [templateService]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    // 確保 permissions 是陣列（防禦性編程）
    let permsArray = permissions;
    if (typeof permissions === 'string') {
      try {
        permsArray = JSON.parse(permissions);
      } catch (e) {
        permsArray = [];
      }
    } else if (Array.isArray(permissions)) {
      permsArray = [...permissions];
    } else {
      permsArray = [];
    }

    setCurrentPermissions(permsArray);
    setSelectedPermissions(permsArray);
  }, [permissions]);

  // 選擇 Template：套用該 Template 的 permissions
  const handleTemplateChange = (templateId) => {
    setSelectedTemplateId(templateId);
    setStrippedFromTemplate([]);
    if (!templateId) return;
    const template = templates.find(tpl => tpl.id === templateId);
    if (!template || !Array.isArray(template.permissions)) return;

    // 呼叫端沒指定 grantableBy → 不需做 subset 過濾
    if (grantableBy === null) {
      setSelectedPermissions([...template.permissions]);
      return;
    }

    const { kept, dropped } = filterGrantable(
      template.permissions,
      grantableBy,
      {
        resources: RESOURCES_BY_ROLE[role] || [],
        resourceActions: RESOURCE_ACTIONS_BY_ROLE[role] || {},
        allActions: ACTIONS,
      }
    );
    setSelectedPermissions(kept);
    if (dropped.length > 0) {
      setStrippedFromTemplate(dropped);
    }
  };

  // 刪除 Template
  const handleDeleteTemplate = async (id) => {
    try {
      const result = await templateService.delete(id);
      const check = checkServiceResult(result);
      if (!check.ok) {
        message.error(check.detail || t('common.permissions_editor.delete_template_failed'));
        return;
      }
      if (selectedTemplateId === id) setSelectedTemplateId(null);
      message.success(t('common.permissions_editor.delete_template_success'));
      fetchTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
      message.error(t('common.permissions_editor.delete_template_failed'));
    }
  };

  // 打開 Save as Template Modal：預填當前已選 Template 名稱
  const openSaveModal = () => {
    const currentTpl = templates.find(tpl => tpl.id === selectedTemplateId);
    setTemplateNameInput(currentTpl?.name || '');
    setSaveModalOpen(true);
  };

  // 儲存為 Template
  const handleSaveTemplate = async () => {
    const name = templateNameInput.trim();
    if (!name) return;
    try {
      setTemplateSaving(true);
      const result = await templateService.upsert({
        name,
        permissions: selectedPermissions,
      });
      const check = checkServiceResult(result);
      if (!check.ok) {
        message.error(check.detail || t('common.permissions_editor.save_template_failed'));
        return;
      }
      message.success(t('common.permissions_editor.save_template_success'));
      setSaveModalOpen(false);
      setTemplateNameInput('');
      fetchTemplates();
    } catch (error) {
      console.error('Failed to save template:', error);
      message.error(t('common.permissions_editor.save_template_failed'));
    } finally {
      setTemplateSaving(false);
    }
  };

  // hasChanges: selectedPermissions 與 currentPermissions 是否有差異
  const hasChanges = useMemo(() => {
    if (currentPermissions.length !== selectedPermissions.length) return true;
    const currentSet = new Set(currentPermissions);
    return selectedPermissions.some(p => !currentSet.has(p));
  }, [currentPermissions, selectedPermissions]);

  // 清除所有權限
  const handleClear = () => {
    confirm({
      title: t('common.permissions_editor.confirm_clear_title'),
      icon: <ExclamationCircleOutlined />,
      content: t('common.permissions_editor.confirm_clear_content'),
      okText: t('common.action.confirm'),
      cancelText: t('common.action.cancel'),
      onOk: () => {
        setSelectedPermissions([]);
        setSelectedTemplateId(null);
        setStrippedFromTemplate([]);
      }
    });
  };

  // 保存權限
  const handleSave = () => {
    confirm({
      title: t('common.permissions_editor.confirm_save_title'),
      icon: <ExclamationCircleOutlined />,
      content: t('common.permissions_editor.confirm_save_content'),
      okText: t('common.action.confirm'),
      cancelText: t('common.action.cancel'),
      onOk: async () => {
        try {
          setSaving(true);

          // 確保受保護權限始終包含在內
          const protectedPermissions = (isEditingSelf && role === 'operator')
            ? ['operators:write', 'operators:read']
            : [];

          const permissionsToSave = [...new Set([...selectedPermissions, ...protectedPermissions])];

          await onSave({ permissions: permissionsToSave });

          // 儲存成功後，更新 current 為 selected，清除 diff
          setCurrentPermissions([...selectedPermissions]);
          setStrippedFromTemplate([]);
          message.success(t('common.permissions_editor.save_success'));
        } catch (error) {
          console.error('Failed to save permissions:', error);
          message.error(t('common.permissions_editor.save_failed'));
        } finally {
          setSaving(false);
        }
      }
    });
  };

  if (!canEdit) {
    return (
      <Card>
        <Alert
          message={t('common.permissions_editor.no_permission_title')}
          description={isEditingSelf
            ? t('common.permissions_editor.cannot_edit_self_permissions')
            : t('common.permissions_editor.no_permission_desc')}
          type="warning"
          showIcon
          icon={<LockOutlined />}
        />
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <EditOutlined />
          {t('common.permissions_editor.title')}
        </Space>
      }
    >
      {/* Permission Template 選擇 */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
          <Text strong style={{ whiteSpace: 'nowrap' }}>{t('common.permissions_editor.select_template')}</Text>
          <Select
            value={selectedTemplateId}
            onChange={handleTemplateChange}
            style={{ flex: 1 }}
            size="large"
            allowClear
            disabled={false}
            placeholder={t('common.permissions_editor.select_template_placeholder')}
            notFoundContent={t('common.permissions_editor.no_templates')}
            optionLabelProp="label"
          >
            {templates.map(tpl => (
              <Select.Option key={tpl.id} value={tpl.id} label={tpl.name}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>{tpl.name}</span>
                  <Popconfirm
                    title={t('common.permissions_editor.delete_template_confirm', { name: tpl.name })}
                    okText={t('common.action.confirm')}
                    cancelText={t('common.action.cancel')}
                    onConfirm={(e) => {
                      e?.stopPropagation?.();
                      handleDeleteTemplate(tpl.id);
                    }}
                    onCancel={(e) => e?.stopPropagation?.()}
                  >
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Popconfirm>
                </div>
              </Select.Option>
            ))}
          </Select>
          {(() => {
            const currentSet = new Set(currentPermissions);
            const selectedSet = new Set(selectedPermissions);
            const addedCount = [...selectedSet].filter(p => !currentSet.has(p)).length;
            const removedCount = [...currentSet].filter(p => !selectedSet.has(p)).length;
            const strippedCount = strippedFromTemplate.length;
            return (addedCount > 0 || removedCount > 0 || strippedCount > 0) ? (
              <Space size={8}>
                {addedCount > 0 && (
                  <Tag color="green">{t('common.permissions_editor.diff_added', { count: addedCount })}</Tag>
                )}
                {removedCount > 0 && (
                  <Tag color="red">{t('common.permissions_editor.diff_removed', { count: removedCount })}</Tag>
                )}
                {strippedCount > 0 && (
                  <Tooltip title={strippedFromTemplate.join(', ')}>
                    <Tag color="orange">
                      {t('common.permissions_editor.template_stripped_count', { count: strippedCount })}
                    </Tag>
                  </Tooltip>
                )}
              </Space>
            ) : null;
          })()}
        </div>
      </div>

      {/* Save as Template Modal */}
      <Modal
        title={t('common.permissions_editor.save_as_template')}
        open={saveModalOpen}
        onOk={handleSaveTemplate}
        onCancel={() => setSaveModalOpen(false)}
        confirmLoading={templateSaving}
        okText={t('common.action.confirm')}
        cancelText={t('common.action.cancel')}
        okButtonProps={{ disabled: !templateNameInput.trim() }}
      >
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">{t('common.permissions_editor.save_as_template_overwrite_hint')}</Text>
        </div>
        <Input
          placeholder={t('common.permissions_editor.template_name_placeholder')}
          value={templateNameInput}
          onChange={(e) => setTemplateNameInput(e.target.value)}
          onPressEnter={(e) => {
            // 中文 IME 選字按 Enter 會被 antd onPressEnter 當成送出 → 忽略 composition 中的 Enter
            if (e.nativeEvent.isComposing) return
            handleSaveTemplate()
          }}
          maxLength={50}
        />
      </Modal>

      <Divider>{t('common.permissions_editor.or_select_manually')}</Divider>

      <PermissionMatrix
        value={selectedPermissions}
        onChange={(newPermissions) => {
          setSelectedPermissions(newPermissions);
          setSelectedTemplateId(null);
          setStrippedFromTemplate([]);
        }}
        role={role}
        isEditingSelf={isEditingSelf}
        diffSource={currentPermissions}
        grantableBy={grantableBy}
      />

      {/* 底部佔位，避免固定按鈕遮擋內容 */}
      {hasChanges && <div style={{ height: 60 }} />}

      {/* 保存按鈕（固定底部） */}
      {hasChanges && (
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: '#fff',
            padding: '12px 16px',
            borderTop: '1px solid #f0f0f0',
            textAlign: 'right',
            marginLeft: -24,
            marginRight: -24,
            marginBottom: -24,
            zIndex: 10
          }}
        >
          <Space>
            <Button
              icon={<UndoOutlined />}
              onClick={() => {
                setSelectedPermissions([...currentPermissions]);
                setSelectedTemplateId(null);
                setStrippedFromTemplate([]);
              }}
            >
              {t('common.button.reset')}
            </Button>
            <Button
              icon={<UndoOutlined />}
              onClick={handleClear}
              danger
            >
              {t('common.permissions_editor.clear_all')}
            </Button>
            <Button icon={<PlusOutlined />} onClick={openSaveModal}>
              {t('common.permissions_editor.save_as_template')}
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
            >
              {t('common.button.save')}
            </Button>
          </Space>
        </div>
      )}
    </Card>
  );
};

export default PermissionsEditor;
